// 1. React and DOM imports

// 2. External libraries
import { useQuery, useQueryClient } from '@tanstack/react-query';

// 3. Internal components

// 4. Type imports
import type { UseQueryResult } from '@tanstack/react-query';
import type { ConnectionResponse, ListTopicsResponse, TopicResponse } from '~/generated/api/model';

// 5. Custom hooks and utilities
import { listTopicsV1TopicsGet } from '~/generated/api/topics/topics';
import { queryKeys } from './queryKeys';

/** Maximum page size accepted by GET /v1/topics (backend caps `n` at 100). */
const TOPICS_PAGE_SIZE = 100;

export function useTopicsQuery(): UseQueryResult<ListTopicsResponse, Error> {
  const queryClient = useQueryClient();

  return useQuery<ListTopicsResponse, Error, ListTopicsResponse, typeof queryKeys.topics.all>({
    queryKey: queryKeys.topics.all,
    // Seeds sibling caches read-only-if-absent (never overwrites existing
    // entries); consumers must not rely on seeding for freshness.
    queryFn: async () => {
      // Page through GET /v1/topics until every topic is accumulated. The
      // endpoint caps page size at 100, so larger workspaces span multiple
      // requests.
      const accumulatedTopics: TopicResponse[] = [];
      let total: number;
      let page = 0;

      for (;;) {
        const response = await listTopicsV1TopicsGet({ page, pageSize: TOPICS_PAGE_SIZE });
        total = response.total;

        if (response.topics.length === 0) {
          break;
        }

        accumulatedTopics.push(...response.topics);

        // Pre-populate caches to avoid redundant API calls
        // Only pre-populate if not already cached to avoid overwriting pending edits
        response.topics.forEach((topic: TopicResponse) => {
          if (topic.connections && topic.connections.length > 0) {
            // Seed connections list cache for this topic
            const existingConnections = queryClient.getQueryData(
              queryKeys.connections.list(topic.id, undefined)
            );
            if (!existingConnections) {
              queryClient.setQueryData(
                queryKeys.connections.list(topic.id, undefined),
                topic.connections
              );
            }

            // Seed individual connection caches
            topic.connections.forEach((connection: ConnectionResponse) => {
              const existingData = queryClient.getQueryData(
                queryKeys.connection(topic.id, connection.id)
              );
              if (!existingData) {
                queryClient.setQueryData(queryKeys.connection(topic.id, connection.id), connection);
              }
            });
          }
        });

        if (accumulatedTopics.length >= total) {
          break;
        }
        page += 1;
      }

      return {
        page: 0,
        pageSize: accumulatedTopics.length,
        topics: accumulatedTopics,
        total,
      };
    },
  });
}
